import {
  BackButton,
  Card,
  ErrorAlert,
  LoadingAlert,
  Text,
  TradeConfirmationReceipt,
  TradeConfirmationRequest,
} from '@/components';
import { useNavigation } from '@/components/providers/common/NavigationProvider';
import { useTradeableTokens } from '@/components/providers/postauth/TradeableTokensProvider';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import AnimatedLayout from '@/components/templates/AnimatedLayout';
import APP_CONFIG from '@/config/app';
import { TokenInterface } from '@/interfaces';
import { TRADE_CARD, WALLET } from '@/utils/strings';
import {
  FC,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  BuyableAssetType,
  SellableAssetType,
  TradeFormDataInterface,
} from './interfaces/TradeInterfaces';
import TradeForm from './TradeForm';
export interface TradeCardProps {
  className?: string;
  onTokenSelectionChange?: (tokens: TokenInterface[]) => void;
  initialBuyAsset?: TokenInterface;
  isLoading?: boolean;
}
export const SCREENS = {
  INITIAL: 'trade-initial',
  CONFIRMATION: 'trade-confirmation',
  RECEIPT: 'trade-receipt',
};
const CROSS_CHAIN_TRADE_BLOCKCHAINS = ['ethereum', 'base'];
const normalizeBlockchain = (blockchain?: string | null) =>
  typeof blockchain === 'string' ? blockchain.toLowerCase() : '';
const getAllowedBuyBlockchains = (sellBlockchain?: string | null): string[] => {
  const normalizedSellBlockchain = normalizeBlockchain(sellBlockchain);
  return CROSS_CHAIN_TRADE_BLOCKCHAINS.includes(normalizedSellBlockchain)
    ? CROSS_CHAIN_TRADE_BLOCKCHAINS
    : [normalizedSellBlockchain].filter(Boolean);
};
const applyYieldBearingNativeAssetId = (
  tradeData: TradeFormDataInterface,
): TradeFormDataInterface => {
  const normalizedTradeData = { ...tradeData };

  if (normalizedTradeData.sellAsset?.address === '0x0') {
    const asset = normalizedTradeData.sellAsset as any;
    if (asset.id) {
      normalizedTradeData.yieldBearingNativeAssetId = asset.id;
    }
  } else {
    delete normalizedTradeData.yieldBearingNativeAssetId;
  }

  return normalizedTradeData;
};
const TradeCard: FC<TradeCardProps> = ({
  className,
  onTokenSelectionChange,
  initialBuyAsset,
  isLoading = false,
}) => {
  const {
    currentScreen,
    navigateBack,
    navigateBackToStart,
    canNavigateBack,
    navigateForward,
    direction,
  } = useNavigation();
  const {
    getTokensWithPositiveBalance,
    getUnderlyingYieldBearingAssetsWithPositiveBalance,
    isInitialLoading: userPortfolioAssetsInitialLoading,
    error: userPortfolioAssetsError,
    isUserAuthorized,
  } = useWalletsContext();
  const {
    getTokens,
    getFeaturedTokens,
    initialLoading: tradeableTokensInitialLoading,
    error: tradeableTokensError,
  } = useTradeableTokens();
  const onTokenSelectionChangeRef = useRef(onTokenSelectionChange);
  onTokenSelectionChangeRef.current = onTokenSelectionChange;
  const supportedSellAssets = useMemo(
    () =>
      [
        ...getTokensWithPositiveBalance(APP_CONFIG.TRADE_SUPPORTED_NETWORKS),
        ...getUnderlyingYieldBearingAssetsWithPositiveBalance(
          APP_CONFIG.TRADE_SUPPORTED_NETWORKS,
        ),
      ].sort((a, b) => b.balanceInUsd - a.balanceInUsd),
    [
      getTokensWithPositiveBalance,
      getUnderlyingYieldBearingAssetsWithPositiveBalance,
    ],
  );
  const [tradeData, setTradeData] = useState<TradeFormDataInterface>({
    sellAsset: null,
    buyAsset: null,
    amount: 0,
  });
  const getBuyCandidatesForSellBlockchain = useCallback(
    (
      sellBlockchain: string,
      preferredBuyAsset?: BuyableAssetType | null,
    ): BuyableAssetType[] => {
      const allowedBuyBlockchains = getAllowedBuyBlockchains(sellBlockchain);
      const buyCandidates = new Map<string, BuyableAssetType>();
      const addCandidate = (token: TokenInterface) => {
        if (!token.id || buyCandidates.has(token.id)) {
          return;
        }
        buyCandidates.set(token.id, token as BuyableAssetType);
      };

      if (
        preferredBuyAsset &&
        allowedBuyBlockchains.includes(
          normalizeBlockchain(preferredBuyAsset.blockchain),
        )
      ) {
        addCandidate(preferredBuyAsset);
      }

      allowedBuyBlockchains.forEach((blockchain) => {
        const featuredTokens = getFeaturedTokens(undefined, blockchain);
        const chainTokens =
          featuredTokens.length > 0
            ? featuredTokens
            : getTokens('', 0, blockchain);
        chainTokens.forEach(addCandidate);
      });

      return Array.from(buyCandidates.values());
    },
    [getFeaturedTokens, getTokens],
  );
  const resolveTradeSelection = useCallback((): TradeFormDataInterface => {
    if (!supportedSellAssets.length) {
      return {
        sellAsset: null,
        buyAsset: null,
        amount: 0,
      };
    }

    const preferredChain =
      initialBuyAsset &&
      supportedSellAssets.some(
        (asset) =>
          asset.blockchain.toLowerCase() === initialBuyAsset.blockchain.toLowerCase(),
      )
        ? initialBuyAsset.blockchain
        : null;

    const orderedChains = [
      ...(preferredChain ? [preferredChain] : []),
      ...APP_CONFIG.TRADE_SUPPORTED_NETWORKS.filter(
        (chain) => chain !== preferredChain,
      ),
    ];

    for (const chain of orderedChains) {
      const chainSellAssets = supportedSellAssets.filter(
        (asset) => asset.blockchain.toLowerCase() === chain.toLowerCase(),
      );
      if (!chainSellAssets.length) {
        continue;
      }

      const buyCandidates = getBuyCandidatesForSellBlockchain(
        chain,
        initialBuyAsset &&
          getAllowedBuyBlockchains(chain).includes(
            normalizeBlockchain(initialBuyAsset.blockchain),
          )
          ? initialBuyAsset
          : null,
      );

      for (const buyCandidate of buyCandidates) {
        const sellCandidate = chainSellAssets.find(
          (asset) => asset.id !== buyCandidate.id,
        );
        if (sellCandidate) {
          return {
            sellAsset: sellCandidate,
            buyAsset: buyCandidate,
            amount:
              sellCandidate.balance *
              APP_CONFIG.ASSET_TO_SELL_DEFAULT_PERCENTAGE,
          };
        }
      }

      if (buyCandidates.length > 0) {
        const sellCandidate = chainSellAssets[0];
        const buyCandidate =
          buyCandidates.find((token) => token.id !== sellCandidate.id) ||
          buyCandidates[0] ||
          null;
        return {
          sellAsset: sellCandidate,
          buyAsset: buyCandidate,
          amount:
            sellCandidate.balance * APP_CONFIG.ASSET_TO_SELL_DEFAULT_PERCENTAGE,
        };
      }
    }

    const fallbackSellAsset = supportedSellAssets[0];
    return {
      sellAsset: fallbackSellAsset,
      buyAsset: null,
      amount:
        fallbackSellAsset.balance * APP_CONFIG.ASSET_TO_SELL_DEFAULT_PERCENTAGE,
    };
  }, [supportedSellAssets, initialBuyAsset, getBuyCandidatesForSellBlockchain]);
  const getReplacementBuyAsset = useCallback(
    (
      sellAsset: SellableAssetType,
      currentBuyAsset?: BuyableAssetType | null,
    ): BuyableAssetType | null => {
      const allowedBuyBlockchains = getAllowedBuyBlockchains(
        sellAsset.blockchain,
      );
      const preferredBuyAsset =
        currentBuyAsset &&
        allowedBuyBlockchains.includes(
          normalizeBlockchain(currentBuyAsset.blockchain),
        )
          ? currentBuyAsset
          : initialBuyAsset &&
              allowedBuyBlockchains.includes(
                normalizeBlockchain(initialBuyAsset.blockchain),
              )
            ? initialBuyAsset
            : null;

      const buyCandidates = getBuyCandidatesForSellBlockchain(
        sellAsset.blockchain,
        preferredBuyAsset,
      );

      return (
        buyCandidates.find((token) => token.id !== sellAsset.id) ||
        buyCandidates[0] ||
        null
      );
    },
    [getBuyCandidatesForSellBlockchain, initialBuyAsset],
  );
  const setTradeDataIfChanged = useCallback(
    (nextTradeData: TradeFormDataInterface) => {
      setTradeData((prevTradeData) => {
        const normalizedNextTradeData =
          applyYieldBearingNativeAssetId(nextTradeData);
        const sameSellAssetId =
          prevTradeData.sellAsset?.id === normalizedNextTradeData.sellAsset?.id;
        const sameBuyAssetId =
          prevTradeData.buyAsset?.id === normalizedNextTradeData.buyAsset?.id;
        const sameAmount = prevTradeData.amount === normalizedNextTradeData.amount;
        const sameYieldBearingNativeAssetId =
          prevTradeData.yieldBearingNativeAssetId ===
          normalizedNextTradeData.yieldBearingNativeAssetId;
        const sameResponse = prevTradeData.response === normalizedNextTradeData.response;
        if (
          sameSellAssetId &&
          sameBuyAssetId &&
          sameAmount &&
          sameYieldBearingNativeAssetId &&
          sameResponse
        ) {
          return prevTradeData;
        }
        return normalizedNextTradeData;
      });
    },
    [],
  );
  const resetTradeData = useCallback(() => {
    setTradeDataIfChanged(resolveTradeSelection());
  }, [resolveTradeSelection, setTradeDataIfChanged]);
  useEffect(() => {
    if (!tradeData.sellAsset && !tradeData.buyAsset) {
      setTradeDataIfChanged(resolveTradeSelection());
      return;
    }

    if (!supportedSellAssets.length) {
      setTradeDataIfChanged({
        sellAsset: null,
        buyAsset: null,
        amount: 0,
      });
      return;
    }

    const normalizedSellAsset =
      tradeData.sellAsset &&
      supportedSellAssets.find((asset) => asset.id === tradeData.sellAsset?.id);

    if (!normalizedSellAsset) {
      setTradeDataIfChanged(resolveTradeSelection());
      return;
    }

    const allowedBuyBlockchains = getAllowedBuyBlockchains(
      normalizedSellAsset.blockchain,
    );
    const isCurrentBuyAssetValid =
      !!tradeData.buyAsset &&
      allowedBuyBlockchains.includes(
        normalizeBlockchain(tradeData.buyAsset.blockchain),
      ) &&
      tradeData.buyAsset.id !== normalizedSellAsset.id;
    const replacementBuyAsset = isCurrentBuyAssetValid
      ? tradeData.buyAsset
      : getReplacementBuyAsset(normalizedSellAsset, tradeData.buyAsset);

    if (
      tradeData.sellAsset?.id !== normalizedSellAsset.id ||
      !isCurrentBuyAssetValid
    ) {
      setTradeDataIfChanged({
        ...tradeData,
        sellAsset: normalizedSellAsset,
        buyAsset: replacementBuyAsset,
        amount:
          tradeData.sellAsset?.id === normalizedSellAsset.id
            ? tradeData.amount
            : normalizedSellAsset.balance *
              APP_CONFIG.ASSET_TO_SELL_DEFAULT_PERCENTAGE,
      });
    }
  }, [
    tradeData,
    supportedSellAssets,
    resolveTradeSelection,
    getReplacementBuyAsset,
    setTradeDataIfChanged,
  ]);
  const handleTradeDataChange = useCallback(
    (data: SetStateAction<TradeFormDataInterface>) => {
      setTradeData((prev) => {
        const newTradeData = applyYieldBearingNativeAssetId({
          ...prev,
          ...(typeof data === 'function' ? data(prev) : data),
        });
        if (newTradeData.sellAsset?.address === '0x0') {
          const asset = newTradeData.sellAsset as any;
          console.log('Native sell asset detected:', {
            id: asset.id,
            address: asset.address,
            symbol: asset.symbol,
            name: asset.name,
            defiLlamaId: asset.defiLlamaId,
            isUnderlyingYieldBearingAsset: asset.isUnderlyingYieldBearingAsset,
          });
        }
        if (newTradeData.buyAsset?.address === '0x0') {
          const asset = newTradeData.buyAsset as any;
          console.log('Native buy asset detected:', {
            id: asset.id,
            address: asset.address,
            symbol: asset.symbol,
            name: asset.name,
            defiLlamaId: asset.defiLlamaId,
            isUnderlyingYieldBearingAsset: asset.isUnderlyingYieldBearingAsset,
          });
        }
        return newTradeData;
      });
    },
    [],
  );
  const handleSuccessfulTradeReceipt = useCallback(() => {
    resetTradeData();
    navigateBackToStart();
  }, [resetTradeData, navigateBackToStart]);
  const handleNavigateBack = useCallback(() => {
    navigateBack();
  }, [navigateBack]);
  const renderHeaderLeft = useMemo(() => {
    if (currentScreen === SCREENS.INITIAL) {
      return (
        <Text
          size={APP_CONFIG.STYLES.TEXT.BIG_HEADING}
          color={APP_CONFIG.STYLES.TEXT.BIG_HEADING_COLOR}
          value={TRADE_CARD.SCREENS.TRADE.LABELS.HEADER}
        />
      );
    }
    return canNavigateBack ? (
      <BackButton onBackClick={handleNavigateBack} />
    ) : null;
  }, [currentScreen, canNavigateBack, handleNavigateBack]);
  const renderHeaderCenter = useMemo(() => {
    switch (currentScreen) {
      case SCREENS.CONFIRMATION:
        return (
          <Text
            size={APP_CONFIG.STYLES.TEXT.BIG_HEADING}
            color={APP_CONFIG.STYLES.TEXT.BIG_HEADING_COLOR}
            value={TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.LABELS.HEADER}
          />
        );
      case SCREENS.RECEIPT:
        return (
          <Text
            size={APP_CONFIG.STYLES.TEXT.BIG_HEADING}
            color={APP_CONFIG.STYLES.TEXT.BIG_HEADING_COLOR}
            value={TRADE_CARD.SCREENS.TRADE_CONFIRMATION_RECEIPT.LABELS.HEADER}
          />
        );
      default:
        return null;
    }
  }, [currentScreen]);
  const renderCardBody = useMemo(() => {
    if (!isUserAuthorized) {
      return (
        <ErrorAlert
          title={WALLET.UNAUTHORIZED_USER_ERROR_TITLE}
          description={WALLET.UNAUTHORIZED_USER_ERROR_DESCRIPTION}
        />
      );
    }
    const initialLoading =
      userPortfolioAssetsInitialLoading || tradeableTokensInitialLoading;
    const error = userPortfolioAssetsError || tradeableTokensError;
    if (initialLoading) {
      return (
        <LoadingAlert
          title={
            TRADE_CARD.SCREENS.TRADE.MESSAGES.LOADING.WALLETS_LOADING.TITLE
          }
          description={
            TRADE_CARD.SCREENS.TRADE.MESSAGES.LOADING.WALLETS_LOADING
              .DESCRIPTION
          }
        />
      );
    }
    if (error) {
      return (
        <ErrorAlert
          title={
            TRADE_CARD.SCREENS.TRADE.MESSAGES.ERROR.WALLETS_LOADING_ERROR.TITLE
          }
          description={
            TRADE_CARD.SCREENS.TRADE.MESSAGES.ERROR.WALLETS_LOADING_ERROR
              .DESCRIPTION
          }
        />
      );
    }
    switch (currentScreen) {
      case SCREENS.INITIAL:
        return (
          <TradeForm
            tradeData={tradeData}
            setTradeData={handleTradeDataChange}
            handleSuccessfulTradeConfiguration={() =>
              navigateForward(SCREENS.CONFIRMATION)
            }
            isLoading={isLoading}
          />
        );
      case SCREENS.CONFIRMATION:
        return (
          <TradeConfirmationRequest
            tradeData={tradeData}
            handleSuccessfulTradeConfirmation={() =>
              navigateForward(SCREENS.RECEIPT)
            }
          />
        );
      case SCREENS.RECEIPT:
        return (
          <TradeConfirmationReceipt
            tradeData={tradeData}
            handleSuccessfulTradeReceipt={handleSuccessfulTradeReceipt}
          />
        );
      default:
        return null;
    }
  }, [
    currentScreen,
    tradeData,
    handleTradeDataChange,
    handleSuccessfulTradeReceipt,
  ]);
  useEffect(() => {
    if (onTokenSelectionChangeRef.current) {
      const selectedTokens: TokenInterface[] = [];
      if (tradeData.sellAsset) selectedTokens.push(tradeData.sellAsset);
      if (tradeData.buyAsset) selectedTokens.push(tradeData.buyAsset);
      onTokenSelectionChangeRef.current(selectedTokens);
    }
  }, [tradeData]);
  return (
    <Card className={className}>
      <Card.Header left={renderHeaderLeft} center={renderHeaderCenter} />
      <Card.Body>
        <AnimatedLayout uniqueKey={currentScreen} slideFrom={direction}>
          {renderCardBody}
        </AnimatedLayout>
      </Card.Body>
    </Card>
  );
};
export default memo(TradeCard);
